<template>
  <div v-if="view" class="table" @pointerup="dragging = false">
    <!-- Async room: who the turn is waiting on (src/store/asyncPlay.js). -->
    <p v-if="waitingBanner" class="waiting-banner" :class="{ 'is-mine': waitingOnMe }">{{ waitingBanner }}</p>
    <!-- status strip -->
    <div class="table__status">
      <span class="season-chip">{{ seasonName }}</span>
      <span class="time-track" :title="`${timeAfter} of ${threshold} time after this card`">
        <span
          v-for="i in threshold"
          :key="i"
          class="time-pip"
          :class="{ 'time-pip--used': i <= timeBefore, 'time-pip--now': i > timeBefore && i <= timeAfter }"
        />
      </span>
      <span v-if="playing" class="muted table__waiting">
        <template v-if="iHavePlaced && stillDrawing.length">
          waiting on {{ stillDrawing.map((p) => p.name).join(', ') }}
        </template>
      </span>
    </div>

    <!-- the card in play + the queen's edicts -->
    <div class="table__cards">
      <div class="card-stack">
        <img
          v-if="turn"
          class="card-face"
          :src="cardSrc(turn.cardId)"
          :alt="cardName"
          @click="zoomCard = turn.cardId"
        />
        <div v-if="turn?.ruinsIds?.length" class="ruins-chips">
          <img
            v-for="id in turn.ruinsIds"
            :key="id"
            class="ruins-chip"
            :src="cardSrc(id)"
            alt="Ruins"
            @click="zoomCard = id"
          />
        </div>
      </div>
      <div class="edicts">
        <button
          v-for="letter in ['A', 'B', 'C', 'D']"
          :key="letter"
          class="edict"
          :class="{ 'edict--active': activeEdicts.includes(letter) }"
          @click="zoomCard = edictCardId(letter)"
        >
          <span class="edict__letter">{{ letter }}</span>
          <img class="edict__img" :src="cardSrc(edictCardId(letter))" :alt="edictName(letter)" />
        </button>
      </div>
    </div>

    <!-- ambush banner -->
    <div v-if="playing && turn?.type === 'ambush' && !iHavePlaced" class="notice notice--ambush">
      <strong>{{ cardName }}!</strong>
      Draw the monster on {{ victimName }}'s map.
    </div>
    <div v-else-if="playing && turn?.ruins && !iHavePlaced && !options[0]?.fallback" class="notice">
      Ruins: this shape must cover an empty ruins space.
    </div>
    <div v-else-if="playing && options[0]?.fallback && !iHavePlaced" class="notice">
      Nothing fits{{ turn?.ruins ? ' on a ruins space' : '' }} — draw a single
      square anywhere instead.
    </div>

    <!-- my drawing surface -->
    <div class="table__map">
      <MapGrid
        :grid="activeGrid"
        :interactive="playing && !iHavePlaced"
        :ghost="ghost"
        :dragging="dragging"
        @cell="onCell"
      />
    </div>

    <!-- controls -->
    <div class="table__controls">
      <template v-if="playing && !iHavePlaced">
        <div v-if="options.length > 1" class="options-row">
          <button
            v-for="(option, i) in options"
            :key="i"
            class="shape-option"
            :class="{ 'is-selected': i === optionIndex }"
            @click="selectOption(i)"
          >
            <ShapePreview :cells="option.cells" :terrain="option.terrains[0]" />
            <span v-if="option.coin" class="shape-option__coin">🪙</span>
          </button>
        </div>
        <div v-if="terrainChoices.length > 1" class="terrain-row">
          <button
            v-for="t in terrainChoices"
            :key="t"
            class="terrain-option"
            :class="{ 'is-selected': t === terrain }"
            @click="terrain = t"
          >
            <img :src="`/tiles/${t}.png`" :alt="t" />
          </button>
        </div>
        <div class="action-row">
          <button class="secondary action-row__spin" :disabled="!canTransform" @click="rotate">⟳ Rotate</button>
          <button class="secondary action-row__spin" :disabled="!canTransform" @click="flip">⇋ Flip</button>
          <button class="primary action-row__go" :disabled="!ghost || !ghost.legal" @click="confirm">
            Draw it
          </button>
        </div>
      </template>
      <p v-else-if="playing" class="muted table__done-line">
        Drawn. {{ stillDrawing.length ? 'Waiting for the table…' : '' }}
      </p>
    </div>

    <!-- the rest of the table: a drawer, so the boards never crowd the
         controls off the screen (Matt's ask, bug report 2026-08-29) -->
    <div class="others">
      <button
        class="others__bar"
        :aria-expanded="drawerOpen ? 'true' : 'false'"
        @click="drawerOpen = !drawerOpen"
      >
        <span class="others__mine">You · {{ runningTotal(mySeat) }}★ · {{ myCoins }}🪙</span>
        <span class="others__label">
          {{ others.length }} other {{ others.length === 1 ? 'player' : 'players' }}
          <span class="others__chevron" :class="{ 'others__chevron--open': drawerOpen }">▾</span>
        </span>
      </button>
      <div v-if="drawerOpen" class="others__boards">
        <button
          v-for="p in others"
          :key="p.id"
          class="opponent"
          @click="zoomMap = p.seat"
        >
          <MapGrid :grid="view.maps[p.seat].grid" small />
          <span class="opponent__name">
            {{ p.name }}
            <span v-if="isWaitingOn(p)" class="opponent__pending">✏️</span>
          </span>
          <span class="opponent__score muted">{{ runningTotal(p.seat) }}★ · {{ view.maps[p.seat].coins }}🪙</span>
        </button>
      </div>
    </div>

    <!-- overlays -->
    <!-- data-pad: a controller's A closes it too; Escape (a controller's B) closes either. -->
    <div v-if="zoomCard" class="overlay" data-pad @click="zoomCard = null">
      <img class="overlay__card" :src="cardSrc(zoomCard)" alt="" />
    </div>

    <div v-if="zoomMap !== null" class="overlay" @click="zoomMap = null">
      <div class="overlay__map" @click.stop>
        <h3>{{ players[zoomMap]?.name }}</h3>
        <MapGrid :grid="view.maps[zoomMap].grid" />
        <button class="secondary" @click="zoomMap = null">Close</button>
      </div>
    </div>

    <ScoreBoard
      v-if="seasonModal !== null"
      :view="view"
      :players="players"
      :season-index="seasonModal"
      @close="seasonModal = null"
    />
    <ScoreBoard
      v-if="view.phase === 'over' && seasonModal === null"
      :view="view"
      :players="players"
      final
      @close="$emit('leave')"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { waitingLine, waitingOnMe as isWaitingOnMe } from '@/store/asyncPlay.js';
import MapGrid from '@/components/game/MapGrid.vue';
import ShapePreview from '@/components/game/ShapePreview.vue';
import ScoreBoard from '@/components/game/ScoreBoard.vue';
import { optionsForTurn, drawerOfMap } from '@/engine/game';
import { fitsAt, normalize } from '@/engine/board';
import { cardById, scoringCardByKey, SEASONS } from '@/engine/cards';
import { registerBugUi } from '@/utils/bugContext';

export default {
  name: 'GameTable',
  components: { MapGrid, ShapePreview, ScoreBoard },
  emits: ['leave'],
  data() {
    return {
      optionIndex: 0,
      shapeCells: null, // the shape as currently rotated/flipped (normalised)
      terrain: null,
      anchor: null,
      dragging: false,
      zoomCard: null,
      zoomMap: null,
      seasonModal: null,
      seasonsSeen: 0,
      drawerOpen: false,
    };
  },
  computed: {
    waitingOnMe() { return isWaitingOnMe(this.$store.state.roomMeta, this.$store.state.user?.uid); },
    waitingBanner() {
      const meta = this.$store.state.roomMeta;
      if (!meta?.async || !this.view) return '';
      const nameOf = (id) => (this.players || []).find((p) => p.id === id)?.name || 'someone';
      return waitingLine(meta, this.$store.state.user?.uid, nameOf);
    },
    ...mapGetters(['view', 'players', 'mySeat', 'myMapSeat', 'iHavePlaced', 'stillDrawing', 'placements']),
    playing() { return this.view?.phase === 'playing'; },
    turn() { return this.view?.turn; },
    cardName() { return this.turn ? cardById(this.turn.cardId)?.name : ''; },
    activeGrid() {
      if (!this.view) return [];
      const seat = this.playing ? this.myMapSeat : this.mySeat;
      return this.view.maps[seat >= 0 ? seat : 0].grid;
    },
    myCoins() {
      const seat = this.mySeat >= 0 ? this.mySeat : 0;
      return this.view?.maps[seat]?.coins ?? 0;
    },
    victimName() {
      return this.players[this.myMapSeat]?.name || 'your neighbour';
    },
    options() {
      if (!this.playing || !this.turn) return [];
      return optionsForTurn(this.turn, this.activeGrid);
    },
    terrainChoices() { return this.options[this.optionIndex]?.terrains || []; },
    canTransform() { return (this.shapeCells?.length || 0) > 1; },
    seasonInfo() {
      const si = this.turn?.season ?? (SEASONS.length - 1);
      return this.view?.script.seasons[si] || null;
    },
    seasonName() { return this.seasonInfo?.name || ''; },
    threshold() { return this.seasonInfo?.threshold || 0; },
    timeAfter() { return this.turn?.timeAfter ?? this.threshold; },
    timeBefore() {
      if (!this.turn) return this.threshold;
      const card = cardById(this.turn.cardId);
      return this.timeAfter - (card?.time || 0);
    },
    activeEdicts() { return this.seasonInfo?.edicts || []; },
    others() {
      return this.players.filter((p) => p.seat !== this.mySeat);
    },
    ghost() {
      if (!this.playing || this.iHavePlaced || !this.shapeCells || !this.anchor) return null;
      const cells = this.placedCells();
      if (!cells) return null;
      const needsRuin = Boolean(this.turn.ruins && this.turn.type === 'explore'
        && !this.options[this.optionIndex]?.fallback);
      return {
        cells,
        terrain: this.terrain,
        legal: fitsAt(this.activeGrid, cells, { needsRuin }),
      };
    },
  },
  watch: {
    'view.turnIndex': { handler() { this.resetSelection(); }, immediate: true },
    // Lifetime stats. `immediate` matters as much as the watch: a client
    // that mounts straight into a finished game (a refresh on the final
    // table, or resuming a saved game that had already ended) never sees
    // the phase CHANGE, and that game would otherwise never be counted.
    // Recording is idempotent, so firing on every arrival is free.
    'view.phase': {
      handler(phase) {
        if (phase === 'over') this.$store.dispatch('recordMyStats');
      },
      immediate: true,
    },
    'view.seasonScores.length': {
      // The first OBSERVATION is detected by oldCount being undefined, not
      // by seasonsSeen being 0 - the old guard swallowed Spring's
      // interstitial entirely (seasonsSeen was still 0 when the count went
      // 0 -> 1, so the first scoring flashed past unseen; bug report
      // 2026-08-29). A fresh mount mid-game still skips the replay of old
      // interstitials, because it lands in the oldCount === undefined arm.
      handler(count, oldCount) {
        if (count === undefined) return;
        if (oldCount === undefined) {
          // First sight of this game (fresh mount or resume): don't replay
          // old interstitials, just note where we are.
          this.seasonsSeen = count;
          return;
        }
        if (count > this.seasonsSeen) {
          this.seasonsSeen = count;
          this.seasonModal = count - 1;
        }
      },
      immediate: true,
    },
  },
  mounted() {
    // What's on the drawing surface right now, for bug reports.
    this.unregisterBugUi = registerBugUi('gameTable', () => ({
      cardName: this.cardName || null,
      optionIndex: this.optionIndex,
      terrain: this.terrain,
      shapeCellCount: this.shapeCells ? this.shapeCells.length : null,
      anchor: this.anchor,
      dragging: this.dragging,
      zoomCard: this.zoomCard,
      zoomMap: this.zoomMap,
      seasonModal: this.seasonModal,
      drawerOpen: this.drawerOpen,
      waitingBanner: this.waitingBanner || null,
    }));
    this.onEscape = (e) => {
      if (e.key !== 'Escape' || (this.zoomCard === null && this.zoomMap === null)) return;
      this.zoomCard = null;
      this.zoomMap = null;
    };
    window.addEventListener('keydown', this.onEscape);
  },
  beforeUnmount() {
    this.unregisterBugUi?.();
    window.removeEventListener('keydown', this.onEscape);
  },
  methods: {
    cardSrc(id) { return `/cards/${id}.jpg`; },
    edictCardId(letter) {
      const key = this.view?.script.edicts[letter];
      return key ? scoringCardByKey(key).id : null;
    },
    edictName(letter) {
      const key = this.view?.script.edicts[letter];
      return key ? scoringCardByKey(key).name : '';
    },
    isWaitingOn(player) {
      if (!this.playing) return false;
      const byMap = this.placements?.[this.view.turnIndex] || {};
      const map = this.view.maps.findIndex(
        (_, m) => drawerOfMap(this.turn, m, this.players.length) === player.seat,
      );
      return map >= 0 && !byMap[map];
    },
    runningTotal(seat) { return this.view?.totals[seat] ?? 0; },
    resetSelection() {
      this.optionIndex = 0;
      this.anchor = null;
      this.dragging = false;
      const option = this.options[0];
      this.shapeCells = option ? normalize(option.cells) : null;
      this.terrain = option ? option.terrains[0] : null;
    },
    selectOption(i) {
      this.optionIndex = i;
      const option = this.options[i];
      this.shapeCells = normalize(option.cells);
      if (!option.terrains.includes(this.terrain)) this.terrain = option.terrains[0];
    },
    rotate() {
      this.shapeCells = normalize(this.shapeCells.map(([r, c]) => [c, -r]));
    },
    flip() {
      this.shapeCells = normalize(this.shapeCells.map(([r, c]) => [r, -c]));
    },
    onCell({ r, c }) {
      this.anchor = { r, c };
      this.dragging = true;
    },
    // The shape's bounding box centred on the anchor, clamped inside the map.
    placedCells() {
      if (!this.shapeCells) return null;
      const maxR = Math.max(...this.shapeCells.map(([r]) => r));
      const maxC = Math.max(...this.shapeCells.map(([, c]) => c));
      let dr = this.anchor.r - Math.floor(maxR / 2);
      let dc = this.anchor.c - Math.floor(maxC / 2);
      dr = Math.max(0, Math.min(dr, 10 - maxR));
      dc = Math.max(0, Math.min(dc, 10 - maxC));
      return this.shapeCells.map(([r, c]) => [r + dr, c + dc]);
    },
    confirm() {
      if (!this.ghost?.legal) return;
      this.$store.dispatch('place', {
        cells: this.ghost.cells,
        terrain: this.terrain,
        fallback: Boolean(this.options[this.optionIndex]?.fallback),
      });
      this.anchor = null;
    },
  },
};
</script>

<style scoped>
.waiting-banner { margin: 0; padding: 0.5rem 0.9rem; border-radius: 0.6rem; background: rgba(0,0,0,0.08); font-size: 0.95rem; }
.waiting-banner.is-mine { background: rgba(0,0,0,0.16); font-weight: 700; }
</style>
